import React, { useState, useEffect, PropsWithChildren } from "react";
import { useApolloClient } from "@apollo/client";
import { View } from "react-native";
import gql from "graphql-tag";

import initializeFeatures from "./initializeFeatures";
import FeatureFlagContext from "./context";
import type { CurrentMember } from "./types";
import { FlagRecord } from "./flags";
import { CURRENT_MEMBER_FRAGMENT } from "../../screens/fragments";
import useCurrentUser from "../useCurrentUser";
import { isPublicPath } from "../../screens/NavigatorScreen/utils";

export const USER_FEATURE_FLAG_QUERY = gql`
  query UserFeatureFlagQuery {
    currentMember {
      ...CurrentMemberFragment
    }
  }
  ${CURRENT_MEMBER_FRAGMENT}
`;

export type UserQueryData = {
  currentMember: CurrentMember;
};

const FeatureFlagProvider = ({ children }: PropsWithChildren<{}>) => {
  const [featureFlags, setFeatureFlags] = useState<FlagRecord>({});
  const { initialized } = useCurrentUser();
  const client = useApolloClient();
  const [isFlagLoaded, setFlagIsLoaded] = useState(false);
  const appReady = initialized && isFlagLoaded;

  useEffect(() => {
    if (!initialized) return;
    if (isPublicPath()) {
      setFlagIsLoaded(true);
      return;
    }

    const fetchFeatureFlags = async () => {
      try {
        const { data } = await client.query<UserQueryData>({ query: USER_FEATURE_FLAG_QUERY });
        const { currentMember } = data;
        const flags = await initializeFeatures(currentMember);

        if (flags) {
          setFeatureFlags(flags);
        }
        setFlagIsLoaded(true);
      } catch (e) {
        setFlagIsLoaded(true);
      }
    };
    fetchFeatureFlags();
  }, [initialized]);

  return appReady ? (
    <FeatureFlagContext.Provider value={featureFlags}>{children}</FeatureFlagContext.Provider>
  ) : (
    <View testID="app-feature-flag-loader-view" />
  );
};

export default FeatureFlagProvider;
